"use client";

import type { ColumnSizingState } from "@tanstack/react-table";
import type { CSSProperties, SetStateAction } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "@/components/ui/sonner";

export type PersistedColumnDefinition<TColumnId extends string> = {
  id: TColumnId;
  defaultWidth: number;
  minWidth: number;
  maxWidth: number;
};

export function columnWidthStyle(width: number): CSSProperties {
  return {
    width,
    minWidth: width,
    maxWidth: width,
    flex: `0 0 ${width}px`,
  };
}
export function fluidColumnWidthStyle(width: number): CSSProperties {
  return {
    width,
    minWidth: width,
    maxWidth: "none",
    flex: `1 0 ${width}px`,
  };
}

export function clampColumnWidth(
  column: Pick<PersistedColumnDefinition<string>, "minWidth" | "maxWidth">,
  width: number,
): number {
  return Math.min(
    column.maxWidth,
    Math.max(column.minWidth, Math.round(width)),
  );
}

function readStoredColumnSizing(storageKey: string): ColumnSizingState {
  if (typeof window === "undefined") return {};
  try {
    const parsed = JSON.parse(window.localStorage.getItem(storageKey) ?? "{}");
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (Object.fromEntries(
          Object.entries(parsed).filter(
            ([, width]) =>
              typeof width === "number" && Number.isFinite(width) && width > 0,
          ),
        ) as ColumnSizingState)
      : {};
  } catch (error) {
    console.error("[table-sizing] Could not read saved column widths", error);
    return {};
  }
}

export function usePersistedColumnSizing(storageKey: string) {
  const [state, setState] = useState<{
    key: string;
    sizing: ColumnSizingState;
    loaded: boolean;
  }>({ key: storageKey, sizing: {}, loaded: false });
  const latest = useRef(state);
  latest.current = state;
  const persist = useCallback((value: typeof state) => {
    if (!value.loaded) return;
    try {
      window.localStorage.setItem(value.key, JSON.stringify(value.sizing));
    } catch (error) {
      console.error("[table-sizing] Could not save column widths", error);
      toast.error("Column widths could not be saved", {
        id: "column-sizing-storage",
      });
    }
  }, []);

  useEffect(() => {
    if (latest.current.key !== storageKey) persist(latest.current);
    setState({
      key: storageKey,
      sizing: readStoredColumnSizing(storageKey),
      loaded: true,
    });
  }, [persist, storageKey]);

  useEffect(() => {
    if (state.key !== storageKey || !state.loaded) return;
    const timer = window.setTimeout(() => persist(state), 150);
    return () => window.clearTimeout(timer);
  }, [persist, state, storageKey]);
  useEffect(() => {
    const flush = () => persist(latest.current);
    window.addEventListener("pagehide", flush);
    return () => {
      window.removeEventListener("pagehide", flush);
      flush();
    };
  }, [persist]);

  const setColumnSizing = useCallback(
    (next: SetStateAction<ColumnSizingState>) => {
      setState((current) => {
        const previous =
          current.key === storageKey
            ? current.sizing
            : readStoredColumnSizing(storageKey);
        return {
          key: storageKey,
          loaded: true,
          sizing: typeof next === "function" ? next(previous) : next,
        };
      });
    },
    [storageKey],
  );
  return [
    state.key === storageKey ? state.sizing : EMPTY_COLUMN_SIZING,
    setColumnSizing,
  ] as const;
}
const EMPTY_COLUMN_SIZING: ColumnSizingState = {};

export function defaultPersistentColumnSizing<TColumnId extends string>(
  columns: readonly PersistedColumnDefinition<TColumnId>[],
): Record<TColumnId, number> {
  return Object.fromEntries(
    columns.map((column) => [column.id, column.defaultWidth]),
  ) as Record<TColumnId, number>;
}

function readStoredPersistentColumnSizing<TColumnId extends string>(
  storageKey: string,
  columns: readonly PersistedColumnDefinition<TColumnId>[],
): Record<TColumnId, number> {
  const fallback = defaultPersistentColumnSizing(columns);
  if (typeof window === "undefined") return fallback;
  try {
    const parsed = JSON.parse(window.localStorage.getItem(storageKey) ?? "{}");
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed))
      return fallback;
    const next = { ...fallback };
    for (const column of columns) {
      const width = (parsed as Record<string, unknown>)[column.id];
      if (typeof width === "number" && Number.isFinite(width)) {
        next[column.id] = clampColumnWidth(column, width);
      }
    }
    return next;
  } catch {
    return fallback;
  }
}

export function usePersistedFixedColumnSizing<TColumnId extends string>(
  storageKey: string,
  columns: readonly PersistedColumnDefinition<TColumnId>[],
) {
  const [columnSizing, setColumnSizing] = useState<Record<TColumnId, number>>(
    () => defaultPersistentColumnSizing(columns),
  );
  const [hasLoadedStoredSizing, setHasLoadedStoredSizing] = useState(false);

  useEffect(() => {
    setColumnSizing(readStoredPersistentColumnSizing(storageKey, columns));
    setHasLoadedStoredSizing(true);
  }, [columns, storageKey]);

  useEffect(() => {
    if (!hasLoadedStoredSizing || typeof window === "undefined") return;
    window.localStorage.setItem(storageKey, JSON.stringify(columnSizing));
  }, [columnSizing, hasLoadedStoredSizing, storageKey]);

  return [columnSizing, setColumnSizing] as const;
}
